import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
// Importeer axios niet meer direct hier als het alleen voor fetchUser was
// import axios from "axios"; 
import oneSpaceApi from "../lib/onespace-api"; // Importeer de API wrapper
import storage from "../lib/storage"; // Import storage abstraction
import { deleteCookie } from "../lib/cookies"; // Import cookie delete functie

// Definieer een interface voor het User object
interface User {
  id: number; // Of string, afhankelijk van je backend
  workos_id: string;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar: string;
  is_admin: boolean;
  tenant_uuid: string | null;
  // Voeg eventueel andere relevante user velden toe
}

// Interface for the combined localStorage data
interface LocalStorageData {
  auth_token: string;
  tenant_uuid: string;
  tenant_api: string;
  [key: string]: any; // Allow for additional fields
}

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  user: User | null; // Voeg user toe aan de context type
  login: (token: string, tenantUuid: string, tenantApi: string) => void;
  logout: () => void;
  isLoadingUser: boolean; // Optioneel: om laadstatus aan te geven
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null); // State voor user data
  const [isLoadingUser, setIsLoadingUser] = useState(true); // Start met laden

  // Add request tracking to prevent duplicate API calls
  const isUserFetchPending = useRef(false);

  const fetchUser = useCallback(async () => {
    // Get auth data from combined localStorage JSON
    const localStorageJson = await storage.getItem("localStorage");
    let currentToken = null;
 
    if (localStorageJson) {
      try {
        const parsedData = JSON.parse(localStorageJson) as LocalStorageData;
        currentToken = parsedData.auth_token;
      } catch (error) {
        console.error("Error parsing localStorage JSON:", error);
      }
    }
    
    // If token not found in combined storage, try the new prefixed key
    if (!currentToken) {
      currentToken = await storage.getItem("onespace_token");
    }
    
    // Fallback to old approach for backward compatibility
    if (!currentToken) {
      currentToken = await storage.getItem("auth_token");
    }

    if (!currentToken) {
      setIsLoadingUser(false);
      return;
    }

    // Prevent duplicate API calls
    if (isUserFetchPending.current) {
      console.log('User fetch already in progress, skipping duplicate request');
      return;
    }

    try {
      isUserFetchPending.current = true; // Set flag before API call
      setIsLoadingUser(true);
      
      const response = await oneSpaceApi.get<User>('/api/user');
      setUser(response.data);
      setIsAuthenticated(true); 
      console.log('User data fetched via oneSpaceApi:', response.data);
      
      // Store user info in storage for other components to use
      await storage.setItem('user_info', JSON.stringify(response.data));
    } catch (error) {
      console.error("Fout bij ophalen gebruiker via oneSpaceApi:", error);
      // logout();
    } finally {
      setIsLoadingUser(false);
      isUserFetchPending.current = false; // Reset flag after API call
    }
  }, []);

  // Effect om token te checken en user te fetchen bij mount
  useEffect(() => {
    const checkToken = async () => {
      // Try to get token from combined localStorage JSON first
      const localStorageJson = await storage.getItem("localStorage");
      let storedToken = null;
      
      storedToken = await storage.getItem("onespace_token");

      
      if (storedToken) {
        setToken(storedToken);
        fetchUser(); // Haal gebruiker op als er een token is
      } else {
        setIsLoadingUser(false); // Geen token, dus niet laden
      }
    };
    
    checkToken();
  }, [fetchUser]); // Afhankelijk van fetchUser

  const login = async (newToken: string, tenantUuid: string, tenantApi: string) => {
    // Store authentication data in the combined localStorage JSON
    const authData: LocalStorageData = {
      auth_token: newToken,
      tenant_uuid: tenantUuid,
      tenant_api: tenantApi
    };
    
    // Store in combined localStorage JSON
    await storage.setItem("localStorage", JSON.stringify(authData));
    
    // Also store in individual keys with the new prefix
    await storage.setItem("onespace_token", newToken);
    await storage.setItem("onespace_tenant_uuid", tenantUuid);
    await storage.setItem("onespace_external_api", tenantApi);
    
    // For backward compatibility, also store in the old keys
    // This can be removed in the future when all clients are updated
    await storage.setItem("auth_token", newToken);
    await storage.setItem("tenant_uuid", tenantUuid);
    await storage.setItem("tenant_api", tenantApi);
    
    setToken(newToken);
    fetchUser();
  };

  const logout = async () => {
    setToken(null);
    setUser(null); 
    setIsAuthenticated(false);
    
    // Verwijder specifieke keys via de storage abstraction (die localStorage/sessionStorage afhandelt)
    console.log('Clearing specific storage keys...');
    await storage.removeItem("localStorage"); // Verwijder gecombineerde JSON
    await storage.removeItem("onespace_token");
    await storage.removeItem("onespace_tenant_uuid");
    await storage.removeItem("onespace_external_api");
    await storage.removeItem("auth_token"); // Legacy
    await storage.removeItem("tenant_uuid"); // Legacy
    await storage.removeItem("tenant_api"); // Legacy
    await storage.removeItem("onespace_settings_cache"); // Dashboard cache
    await storage.removeItem("onespace_settings_timestamp"); // Dashboard cache timestamp
    await storage.removeItem("onespace_theme_styles"); // Theme cache
    await storage.removeItem("onespace_theme_styles_timestamp"); // Theme cache timestamp
    await storage.removeItem("onespace_background_image"); // Oude background image cache
    await storage.removeItem("onespace_background_image_url"); // Oude background image url
    
    // Wis *volledige* localStorage en sessionStorage (wees voorzichtig hiermee)
    // Als je zeker weet dat er geen andere belangrijke data in staat
    console.log('Clearing entire localStorage and sessionStorage...');
    try {
        if (typeof localStorage !== 'undefined') localStorage.clear();
        if (typeof sessionStorage !== 'undefined') sessionStorage.clear();
    } catch (e) {
        console.error("Error clearing web storage:", e);
    }

    // Verwijder relevante cookies
    console.log('Deleting relevant cookies...');
    deleteCookie('demo'); // Verwijder de demo cookie
    // Voeg hier andere relevante cookie namen toe die je wilt verwijderen
    // deleteCookie('session_cookie_name'); 
    // deleteCookie('another_app_cookie');

    // Optioneel: roep een API endpoint aan om de server-side sessie te vernietigen
    try {
        // await oneSpaceApi.post('/api/logout');
    } catch (error) {
        console.error("Error logging out on server:", error);
    }

    console.log('User logged out and storage cleared');
    // Navigeer de gebruiker eventueel naar de login pagina
    // window.location.href = '/login'; 
  };

  // Geef user en isLoadingUser mee in de context value
  return (
    <AuthContext.Provider value={{ isAuthenticated, token, user, login, logout, isLoadingUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}; 